// The Wheel of Fortune: the game's sixteen tiles around a torch-lit hall, one amulet per spin, uniform odds.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { art } from "@/kit/art";
import { TitleBand } from "@/kit/city";
import { Sheet } from "@/kit/overlays";
import { Button, Card, CardHead, Medallion, Row, RowText, cx } from "@/kit/primitives";
import { usePrompts } from "@/state/prompts";
import { RESOURCE_NAMES } from "@/game/constants";
import { fmtNum } from "@/game/format";
import { fmtClock } from "@/game/time";
import { WHEEL_TILES, WORLD } from "@/game/rules";
import { CityPage, useCity } from "./CityRoute";

type Tile = (typeof WHEEL_TILES)[number];
const tileIcon = (t: Tile) => (t.type === "speedup15" ? art.store.speedup15 : t.type === "amulet" ? art.store.amulet : art.store[t.type === "r_w" ? "buy_wood" : t.type === "r_c" ? "buy_clay" : "buy_iron"]);
const tileLabel = (t: Tile) => (t.type === "speedup15" ? "15 min" : t.type === "amulet" ? `× ${t.amount}` : fmtNum(t.amount));
const prizeName = (t: Tile) => (t.type === "speedup15" ? "15-min speed-up" : t.type === "amulet" ? `${t.amount} amulets` : `${fmtNum(t.amount)} ${RESOURCE_NAMES[t.type]}`);
/** grid cell (row, col) for tile index i, clockwise from the top-left */
const CELL: [number, number][] = [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4], [1, 4], [2, 4], [3, 4], [4, 4], [4, 3], [4, 2], [4, 1], [4, 0], [3, 0], [2, 0], [1, 0]];

export function Wheel() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const [spinning, setSpinning] = useState(false);
  const [lit, setLit] = useState<number | null>(null);
  const [won, setWon] = useState<number | null>(null);
  const [recent, setRecent] = useState<{ i: number; at: number }[]>([]);
  const [buy, setBuy] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  if (!v) return <CityPage>{null}</CityPage>;
  const amulets = v.amulet?.num_owned ?? 0;

  const spin = async () => {
    if (spinning || amulets <= 0) return;
    setSpinning(true);
    setWon(null);
    const r = await actions.run("spin", () => api.wheel.spin({ wid, vid }));
    if (!r || typeof r.spin_result !== "string") { setSpinning(false); return; }
    const target = Math.max(0, Math.min(15, Number(r.spin_result)));
    // run the light around the ring, slowing down, and stop on the result
    const steps = 32 + target;
    let k = 0;
    const tick = () => {
      setLit(k % 16);
      k++;
      if (k > steps) { setLit(target); setWon(target); setSpinning(false); setRecent((rs) => [{ i: target, at: Date.now() }, ...rs].slice(0, 6)); return; }
      const t = k / steps;
      timer.current = window.setTimeout(tick, 40 + 260 * t * t);
    };
    tick();
  };

  const tile = won !== null ? WHEEL_TILES[won] : null;
  return (
    <CityPage>
      <TitleBand title="Wheel of Fortune" sub="One amulet per spin" onBack={() => nav(base)} right="info" onRight={() => usePrompts.getState().dialogShow("Prize odds", "Every tile is 1 in 16. Resources: 100 of each at 12.5%, 500 and 2,000 at 6.25% each. The 15-minute speed-up sits on three tiles (18.75%); two amulets on one (6.25%). Prizes go to the city you are in.")} />
      <div className="stack">
        <Card className="wheel-card">
          <div className="wheel-grid">
            {WHEEL_TILES.map((t, i) => {
              const [r, c] = CELL[i];
              const big = t.amount === 2000 || t.type === "amulet";
              return (
                <div key={i} className={cx("wtile", big && "big", lit === i && "lit", won !== null && won !== i && "dimmed")} style={{ gridRow: r + 1, gridColumn: c + 1 }}>
                  <img src={tileIcon(t)} alt={prizeName(t)} />
                  <span>{tileLabel(t)}</span>
                </div>
              );
            })}
            <div className="wcenter">
              <img className="hall" src={art.hero.wheelHall} alt="" />
              <div className="inner">
                {tile ? (
                  <>
                    <div className="eyebrow">You won</div>
                    <Medallion src={tileIcon(tile)} alt="" size={120} glow />
                    <div className="prize">{prizeName(tile)}</div>
                    <div className="muted" style={{ font: "400 var(--fs-meta)/1 var(--text)" }}>Delivered to <b className="gold">{v.village?.city_name}</b></div>
                    <Button kind="crimson" wide disabled={amulets <= 0 || spinning} busy={spinning} onClick={spin}>Spin again</Button>
                    <div className="muted" style={{ font: "400 var(--fs-meta)/1 var(--text)" }}><b className="creamt">{fmtNum(amulets)}</b> amulet{amulets === 1 ? "" : "s"} left · <button type="button" className="link" onClick={() => setBuy(true)}>Buy more ›</button></div>
                  </>
                ) : (
                  <>
                    <Medallion src={art.store.amulet} alt="Amulet" size={120} glow />
                    <div className="eyebrow">Amulets owned</div>
                    <div className="count">{fmtNum(amulets)}</div>
                    <Button kind="crimson" wide disabled={amulets <= 0 || spinning} busy={spinning} onClick={spin}>Spin</Button>
                    <Button kind="steel" size="md" wide onClick={() => setBuy(true)}>Buy amulets</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>

        {recent.length ? (
          <Card>
            <CardHead title="Recent spins" aside="Today · 1 in 16 per tile" />
            <div className="recent-spins">
              {recent.map((r, k) => { const t = WHEEL_TILES[r.i]; return <div key={k} className={cx("rs", k === 0 && "lit")}><img src={tileIcon(t)} alt="" /><b>{prizeName(t)}</b><span className="muted">{k === 0 ? "just now" : fmtClock(r.at)}</span></div>; })}
            </div>
          </Card>
        ) : (
          <Card>
            <CardHead title="Prize odds" aside={<>Every tile is 1 in 16 · paid to <b className="gold">{v.village?.city_name}</b></>} />
            <div className="odds">
              <table className="tbl">
                <thead><tr><th /><th>Wood</th><th>Clay</th><th>Iron</th></tr></thead>
                <tbody>
                  {[[100, "12.5%"], [500, "6.25%"], [2000, "6.25%"]].map(([n, p]) => <tr key={String(n)}><td className="name">{fmtNum(Number(n))}</td><td>{p}</td><td>{p}</td><td>{p}</td></tr>)}
                </tbody>
              </table>
              <div className="rows">
                <Row><Medallion src={art.store.speedup15} alt="" size={48} /><RowText line="15-min speed-up" meta="3 tiles" /><div className="right"><div className="val"><b>18.75%</b></div></div></Row>
                <Row><Medallion src={art.store.amulet} alt="" size={48} /><RowText line="2 amulets" meta="1 tile · a free re-spin" /><div className="right"><div className="val"><b>6.25%</b></div></div></Row>
              </div>
            </div>
          </Card>
        )}
      </div>
      <BuyAmuletsSheet open={buy} onClose={() => setBuy(false)} />
    </CityPage>
  );
}

function BuyAmuletsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { v, wid, vid, actions } = useCity();
  if (!v) return null;
  const item = v.store?.items?.amulet;
  const cost = item?.cost ?? WORLD.amuletCost;
  const gold = v.gold?.amount_owned ?? 0;
  return (
    <Sheet open={open} onClose={onClose} label="Buy amulets" title={<h3 className="eyebrow" style={{ margin: "0 0 6px" }}>Buy amulets</h3>}>
      <p className="muted" style={{ margin: "0 0 14px", font: "400 var(--fs-body2)/1.3 var(--text)" }}>{cost} gold each · you have <b className="gold">{fmtNum(gold)}</b> gold and <b className="creamt">{fmtNum(v.amulet?.num_owned ?? 0)}</b> amulets.</p>
      <div className="rows">
        {[1, 5, 10, 25].map((n) => (
          <Row key={n}>
            <Medallion src={art.store.amulet} alt="" size={54} />
            <RowText line={`${n} amulet${n === 1 ? "" : "s"}`} meta={`${n} spin${n === 1 ? "" : "s"}`} />
            <div className="right">
              <button type="button" className="gold-price" disabled={gold < cost * n || actions.isBusy(`amulet:${n}`)} onClick={async () => { const r = await actions.run(`amulet:${n}`, () => api.store.buy({ wid, vid }, "amulet", n), { success: `${n} amulet${n === 1 ? "" : "s"} bought.` }); if (r) onClose(); }}>
                <span className="coin" /> {fmtNum(cost * n)}
              </button>
            </div>
          </Row>
        ))}
      </div>
    </Sheet>
  );
}
